import {
    CallNodeData,
    DelayNodeData,
    NodeData,
    NodeType,
    ServerNode,
    ServerNodeType,
    SMSNodeData,
    EmailNodeData,
    DecisionNodeData,
    DecisionCondition,
    DecisionFieldType,
    ValidationResult,
} from './WorkflowBuilder.types';
import { Edge, Node } from '@xyflow/react';
import { getTerminalSubgraphNodeIds, isTerminalFlowBridgeId } from './terminalCanvasSplit';

const CROSS_TYPE_FIELDS_TO_STRIP: Partial<Record<NodeType, readonly string[]>> = {
    callNode: ['messageContent'],
    smsNode: ['promptId', 'promptName', 'aiPrompt', 'callText'],
    emailNode: ['promptId', 'promptName', 'aiPrompt', 'callText'],
    delayNode: ['promptId', 'promptName', 'aiPrompt', 'callText', 'messageContent'],
    decisionNode: [
        'promptId',
        'promptName',
        'aiPrompt',
        'callText',
        'messageContent',
        'delayType',
        'timeValue',
        'timeUnit',
        'specificDateTime',
        'decisionField',
        'mappings',
    ],
    endNode: [
        'promptId',
        'promptName',
        'callText',
        'messageContent',
        'delayType',
        'timeValue',
        'timeUnit',
        'specificDateTime',
        'decisionField',
        'mappings',
        'conditions',
        'defaultNextNode',
    ],
};

export function stripCrossTypeNodeDataFields(nodeType: NodeType | undefined, data: Record<string, unknown>): void {
    if (!nodeType) return;
    const keys = CROSS_TYPE_FIELDS_TO_STRIP[nodeType];
    if (!keys?.length) return;
    for (const key of keys) {
        delete data[key];
    }
}

/** When prompt fields change on a call node, keep subLabel in sync for the canvas. */
export function applyCallNodePromptSubLabel(updates: Partial<NodeData>): void {
    if (!('promptName' in updates || 'aiPrompt' in updates)) return;
    const displayName =
        (updates as Partial<CallNodeData>).promptName || (updates as Partial<CallNodeData>).aiPrompt || '';
    updates.subLabel = `AI Prompt: ${displayName}`;
}

/**
 * Removes edges that reference the terminal-flow bridge or missing node ids.
 */
export function sanitizeWorkflowEdges<T extends Edge>(edges: T[], nodes: readonly Node<NodeData>[]): T[] {
    if (nodes.length === 0) return edges;
    const idSet = new Set(nodes.map((n) => n.id));
    const next = edges.filter((e) => {
        if (isTerminalFlowBridgeId(e.source) || isTerminalFlowBridgeId(e.target)) return false;
        if (!idSet.has(e.source) || !idSet.has(e.target)) return false;
        return true;
    });
    if (next.length === edges.length) return edges;
    return next;
}

export const mapNodeTypeToReactFlow = (type: ServerNodeType): NodeType => {
    const typeMap: Record<ServerNodeType, NodeType> = {
        DELAY: 'delayNode',
        CALL: 'callNode',
        SMS: 'smsNode',
        EMAIL: 'emailNode',
        LEAD: 'leadNode',
        DECISION: 'decisionNode',
        END: 'endNode',
        UNKNOWN: 'defaultNode',
    };
    return typeMap[type] || 'defaultNode';
};

const mapReactFlowTypeToServer = (type: string): ServerNodeType => {
    const typeMap: Record<string, ServerNodeType> = {
        delayNode: 'DELAY',
        callNode: 'CALL',
        smsNode: 'SMS',
        emailNode: 'EMAIL',
        leadNode: 'LEAD',
        decisionNode: 'DECISION',
        endNode: 'END',
    };
    return typeMap[type] || 'UNKNOWN';
};

export const getNodeTypeLabelForDisplay = (type: string): string => {
    const typeMap: Record<string, string> = {
        delayNode: 'Delay',
        callNode: 'Call',
        smsNode: 'SMS',
        emailNode: 'Email',
        leadNode: 'Lead',
        decisionNode: 'Decision',
        endNode: 'End',
    };

    return typeMap[type] || 'Node';
};

export const getNodeConfigDataFromServer = (node: ServerNode): Partial<NodeData> => {
    const config = node.config || {};

    switch (node.type) {
        case 'DELAY':
            if (config.delay_type === 'TIMESTAMP') {
                const result: Partial<DelayNodeData> = {
                    delayType: 'TIMESTAMP',
                    specificDateTime: config.delay?.timestamp || new Date().toISOString(),
                    timeValue: '30',
                    timeUnit: 'minutes',
                };
                return result;
            } else {
                // Convert unit format
                let timeUnit: 'minutes' | 'hours' | 'days' = 'minutes';
                if (config.delay?.unit === 'MINUTES') timeUnit = 'minutes';
                else if (config.delay?.unit === 'HOURS') timeUnit = 'hours';
                else if (config.delay?.unit === 'DAYS') timeUnit = 'days';

                const result: Partial<DelayNodeData> = {
                    delayType: 'DURATION',
                    specificDateTime: new Date().toISOString(),
                    timeValue: String(config.delay?.duration || 30),
                    timeUnit: timeUnit,
                };
                return result;
            }
        case 'CALL': {
            const promptName = config.promptName || config.aiPrompt || '';
            return {
                promptId: config.promptId || '',
                promptName: promptName,
                aiPrompt: promptName, // For backward compatibility
                callText: config.callText || '',
                subLabel: `AI Prompt: ${promptName}`,
            } as Partial<CallNodeData>;
        }
        case 'SMS': {
            const smsMode = config.smsMode || 'static';
            const result: Partial<SMSNodeData> = {
                smsMode: smsMode as SMSNodeData['smsMode'],
                messageContent: config.smsText || config.messageContent || '',
                langfusePromptId: config.langfusePromptId || '',
                userAbandonedTimeoutHours: config.userAbandonedTimeoutHours,
                agentAbandonedTimeoutHours: config.agentAbandonedTimeoutHours,
                userAbandonedTimeoutMinutes: config.userAbandonedTimeoutMinutes,
                agentAbandonedTimeoutMinutes: config.agentAbandonedTimeoutMinutes,
                userAbandonedTimeoutUnit:
                    config.userAbandonedTimeoutUnit ??
                    (config.userAbandonedTimeoutMinutes != null ? 'minutes' : 'hours'),
                agentAbandonedTimeoutUnit:
                    config.agentAbandonedTimeoutUnit ??
                    (config.agentAbandonedTimeoutMinutes != null ? 'minutes' : 'hours'),
                expectResponse: config.expectResponse ?? true,
                subLabel: smsMode === 'static' ? 'Static' : 'Agentic',
            };
            return result;
        }
        case 'EMAIL': {
            const result: Partial<EmailNodeData> = {
                messageContent: config.smsText || config.messageContent || '',
            };
            return result;
        }
        case 'DECISION': {
            let conditions: DecisionCondition[] = Array.isArray(config.conditions) ? [...config.conditions] : [];
            if (conditions.length === 0 && config.mappings && Object.keys(config.mappings).length > 0) {
                const field = (config.decisionField || 'call_disposition') as DecisionFieldType;
                conditions = Object.entries(config.mappings).map(([value, mapping]) => {
                    const nextNode = typeof mapping === 'string' ? mapping : mapping?.nextNode || '';
                    return {
                        decisionField: field,
                        values: [value],
                        nextNode,
                    };
                });
            }
            return {
                conditions,
                defaultNextNode: config.defaultNextNode ?? null,
                subLabel: undefined,
            } as Partial<DecisionNodeData>;
        }
        case 'END':
            return {};
        default:
            return {};
    }
};

export const createEdgesFromNodes = (serverNodes: ServerNode[]): Edge[] => {
    console.log('Creating edges from nodes:', serverNodes);
    const edges: Edge[] = [];
    const nodeMap: Record<string, ServerNode> = {};

    // First create a map of nodes by ID for quick lookup
    serverNodes.forEach((node) => {
        nodeMap[node.id] = node;
    });

    serverNodes.forEach((sourceNode) => {
        if (sourceNode.type === 'DECISION') {
            const cfg = sourceNode.config;
            if (cfg?.conditions && cfg.conditions.length > 0) {
                cfg.conditions.forEach((cond, index) => {
                    const targetId = cond.nextNode;
                    if (!targetId || targetId === sourceNode.id) return;
                    edges.push({
                        id: `e_${sourceNode.id}_condition-${index}_${targetId}`,
                        source: sourceNode.id,
                        sourceHandle: `condition-${index}`,
                        target: targetId,
                        targetHandle: null,
                        type: 'straight',
                    } as Edge);
                });
                const defaultNext = cfg.defaultNextNode;
                if (defaultNext) {
                    edges.push({
                        id: `e_${sourceNode.id}_default_${defaultNext}`,
                        source: sourceNode.id,
                        sourceHandle: 'default',
                        target: defaultNext,
                        targetHandle: null,
                        type: 'straight',
                    } as Edge);
                }
                return;
            }
            if (cfg?.mappings) {
                const mappings = cfg.mappings;
                Object.entries(mappings).forEach(([key, value]) => {
                    const targetId = typeof value === 'string' ? value : value?.nextNode;
                    if (!targetId || targetId === sourceNode.id) return;
                    edges.push({
                        id: `e_${sourceNode.id}_${key}_${targetId}`,
                        source: sourceNode.id,
                        sourceHandle: key,
                        target: targetId,
                        targetHandle: null,
                        type: 'straight',
                    } as Edge);
                });
                const defaultNext = cfg.defaultNextNode;
                if (defaultNext) {
                    edges.push({
                        id: `e_${sourceNode.id}_default_${defaultNext}`,
                        source: sourceNode.id,
                        sourceHandle: 'default',
                        target: defaultNext,
                        targetHandle: null,
                        type: 'straight',
                    } as Edge);
                }
            }
            return;
        }
        if (sourceNode.config && sourceNode.config.nextNode) {
            // Check if nextNode references a valid node
            if (sourceNode.config.nextNode === sourceNode.id) {
                console.warn(`Node ${sourceNode.id} has nextNode reference to itself, skipping self-loop`);
                return;
            }

            // Verify target node exists in our workflow
            if (!nodeMap[sourceNode.config.nextNode]) {
                console.warn(`Node ${sourceNode.id} references non-existing target node ${sourceNode.config.nextNode}`);
                return;
            }

            const edge: Edge = {
                id: `e_${sourceNode.id}_${sourceNode.config.nextNode}`,
                source: sourceNode.id,
                target: sourceNode.config.nextNode,
                type: 'straight',
            };

            console.log(`Creating edge from ${sourceNode.id} to ${sourceNode.config.nextNode}`);
            edges.push(edge);
        }
    });

    // If no edges were created, try to create logical connections based on node positions
    if (edges.length === 0 && serverNodes.length > 1) {
        console.log('No valid edges found in config, creating logical connections');

        // Sort nodes by Y position (top to bottom)
        const sortedNodes = [...serverNodes].sort((a, b) => {
            const posA = a.uiConfig.position?.y || 0;
            const posB = b.uiConfig.position?.y || 0;
            return posA - posB;
        });

        // Connect nodes in sequence based on vertical position
        for (let i = 0; i < sortedNodes.length - 1; i++) {
            const edge: Edge = {
                id: `e_${sortedNodes[i].id}_${sortedNodes[i + 1].id}`,
                source: sortedNodes[i].id,
                target: sortedNodes[i + 1].id,
                type: 'straight',
            };

            console.log(`Creating logical edge from ${sortedNodes[i].id} to ${sortedNodes[i + 1].id}`);
            edges.push(edge);
        }
    }

    console.log('Final edges created from nodes:', edges);
    return edges;
};

// Helper function to get edge style based on selection state and hover state
export const getEdgeStyle = (
    edge: { id: string; selected?: boolean },
    hoveredEdge: string | null
): React.CSSProperties => {
    const isHovered = hoveredEdge === edge.id;
    return {
        stroke: edge.selected ? '#000' : '#999',
        strokeWidth: isHovered ? 4 : edge.selected ? 3 : 2,
        cursor: 'pointer', // Use pointer cursor to indicate it's clickable/deletable
    };
};

// Function to convert date to EST ISO format
export const toESTIsoString = (date: Date) => {
    // Format the date in EST timezone in ISO format parts
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const dateParts: Record<string, string> = {};

    // Extract the different parts into an object
    parts.forEach((part) => {
        dateParts[part.type] = part.value;
    });

    // Create ISO format string: YYYY-MM-DDThh:mm:ss.sss-05:00
    return `${dateParts.year}-${dateParts.month}-${dateParts.day}T${dateParts.hour}:${dateParts.minute}:${dateParts.second}.000-05:00`;
};

// Define node type label for the title
export const getNodeTypeLabel = (node: Node<NodeData>): string => {
    const typeMap: Record<NodeType, string> = {
        delayNode: 'Delay',
        callNode: 'Call',
        smsNode: 'SMS',
        emailNode: 'Email',
        leadNode: 'Lead',
        decisionNode: 'Decision',
        endNode: 'End',
        defaultNode: 'Node',
        terminalBridgeNode: '',
    };

    return typeMap[node.type as NodeType] || 'Node';
};

/**
 * Entry node for the main workflow (no incoming edges). Terminal subgraph nodes are excluded so
 * `head_node_id` never points at the terminal flow when those nodes appear first in the flat list.
 */
export const findHeadNodeId = (
    nodes: Node<NodeData>[],
    edges: Edge<NodeData>[],
    terminalDecisionNodeId: string | null = null
): string | null => {
    const terminalIds = getTerminalSubgraphNodeIds(nodes, edges, terminalDecisionNodeId);
    const workflowNodes = nodes
        .filter((n) => !isTerminalFlowBridgeId(n.id) && !terminalIds.has(n.id))
        .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
    if (workflowNodes.length === 0) return null;
    const targetNodes = new Set(edges.map((edge) => edge.target));
    const headNode = workflowNodes.find((node) => !targetNodes.has(node.id));
    return headNode ? headNode.id : workflowNodes[0].id;
};

export const convertToServerNodes = (nodes: Node<NodeData>[], edges: Edge[]): ServerNode[] => {
    const edgeMap: Record<string, string[]> = {};
    const edgeMapByHandle: Record<string, Array<{ handle: string; target: string }>> = {};
    edges.forEach((edge) => {
        if (edge.source === edge.target) {
            console.warn(`Skipping self-loop edge for node ${edge.source}`);
            return;
        }
        if (isTerminalFlowBridgeId(edge.source) || isTerminalFlowBridgeId(edge.target)) {
            return;
        }
        if (!edgeMap[edge.source]) edgeMap[edge.source] = [];
        edgeMap[edge.source].push(edge.target);
        if (edge.sourceHandle != null) {
            if (!edgeMapByHandle[edge.source]) edgeMapByHandle[edge.source] = [];
            edgeMapByHandle[edge.source].push({ handle: edge.sourceHandle, target: edge.target });
        }
    });

    return nodes
        .filter((node) => !isTerminalFlowBridgeId(node.id))
        .map((node) => {
            const abs = { x: node.position.x, y: node.position.y };
            const nextNodeIds = edgeMap[node.id] || [];
            const nextNode = nextNodeIds.length > 0 ? nextNodeIds[0] : null;
            const uiConfig = {
                dragging: false,
                height: 48,
                position: abs,
                positionAbsolute: abs,
                selected: false,
                width: 250,
                label: node.data.label || mapReactFlowTypeToServer(node.type || ''),
            };
            const serverNode: ServerNode = {
                id: node.id,
                type: mapReactFlowTypeToServer(node.type || ''),
                uiConfig,
                config: {},
            };
            if (node.type === 'delayNode') {
                const delayData = node.data as DelayNodeData;
                const isTimestamp = delayData.delayType === 'TIMESTAMP';
                serverNode.config = {
                    delay_type: isTimestamp ? 'TIMESTAMP' : 'DURATION',
                    delay: isTimestamp
                        ? { timestamp: delayData.specificDateTime }
                        : {
                              duration: parseInt(delayData.timeValue) || 1,
                              unit:
                                  delayData.timeUnit === 'minutes'
                                      ? 'MINUTES'
                                      : delayData.timeUnit === 'hours'
                                        ? 'HOURS'
                                        : 'DAYS',
                          },
                    nextNode,
                };
            } else if (node.type === 'callNode') {
                const callData = node.data as CallNodeData;
                serverNode.config = {
                    promptId: callData.promptId || '',
                    promptName: callData.promptName || '',
                    aiPrompt: callData.promptName || callData.aiPrompt || '',
                    callText: callData.callText || '',
                    nextNode,
                };
            } else if (node.type === 'smsNode') {
                const smsData = node.data as SMSNodeData;
                serverNode.config = {
                    smsMode: smsData.smsMode || 'static',
                    smsText: smsData.messageContent || '',
                    langfusePromptId: smsData.langfusePromptId || '',
                    userAbandonedTimeoutHours: smsData.userAbandonedTimeoutHours,
                    agentAbandonedTimeoutHours: smsData.agentAbandonedTimeoutHours,
                    userAbandonedTimeoutMinutes: smsData.userAbandonedTimeoutMinutes,
                    agentAbandonedTimeoutMinutes: smsData.agentAbandonedTimeoutMinutes,
                    userAbandonedTimeoutUnit: smsData.userAbandonedTimeoutUnit,
                    agentAbandonedTimeoutUnit: smsData.agentAbandonedTimeoutUnit,
                    expectResponse: smsData.expectResponse ?? true,
                    nextNode,
                };
            } else if (node.type === 'emailNode') {
                const emailData = node.data as EmailNodeData;
                serverNode.config = { messageContent: emailData.messageContent || '', nextNode };
            } else if (node.type === 'decisionNode') {
                const decisionData = node.data as DecisionNodeData;
                const byHandle = edgeMapByHandle[node.id] || [];
                const baseConditions = decisionData.conditions || [];
                const conditions: DecisionCondition[] = baseConditions.map((c, index) => {
                    const edgeForRow = byHandle.find((h) => h.handle === `condition-${index}`);
                    const nextNode = edgeForRow?.target ?? c.nextNode;
                    return {
                        decisionField: c.decisionField,
                        values: [...c.values],
                        nextNode,
                    };
                });
                let defaultNextNode: string | null = decisionData.defaultNextNode ?? null;
                byHandle.forEach(({ handle, target }) => {
                    if (handle === 'default') {
                        defaultNextNode = target;
                    }
                });
                serverNode.config = {
                    conditions,
                    defaultNextNode: defaultNextNode ?? null,
                };
            } else if (node.type === 'endNode') {
                serverNode.config = {};
            } else {
                serverNode.config = { nextNode };
            }
            return serverNode;
        });
};

export const validateWorkflowConnections = (nodes: Node<NodeData>[], edges: Edge[]): ValidationResult => {
    const issues: string[] = [];
    const nodeMap: Record<string, Node<NodeData>> = {};
    nodes.forEach((node) => {
        if (isTerminalFlowBridgeId(node.id)) return;
        nodeMap[node.id] = node;
    });
    const connectionMap: Record<string, string[]> = {};
    edges.forEach((edge) => {
        if (!connectionMap[edge.source]) connectionMap[edge.source] = [];
        connectionMap[edge.source].push(edge.target);
    });
    edges.forEach((edge) => {
        if (edge.source === edge.target) {
            issues.push(`Node "${nodeMap[edge.source]?.data?.label || edge.source}" has a connection to itself.`);
        }
    });
    const workflowNodeCount = nodes.filter((n) => !isTerminalFlowBridgeId(n.id)).length;
    nodes.forEach((node) => {
        if (isTerminalFlowBridgeId(node.id)) return;
        if (node.type === 'endNode') return; // terminal node – no outgoing connections required
        if (!connectionMap[node.id] || connectionMap[node.id].length === 0) {
            const isTargetOfSomeEdge = edges.some((edge) => edge.target === node.id);
            if (!isTargetOfSomeEdge && workflowNodeCount > 1) {
                issues.push(`Node "${node.data.label || node.id}" has no outgoing connections.`);
            }
        }
    });
    Object.keys(connectionMap).forEach((nodeId) => {
        const node = nodeMap[nodeId];
        if (node?.type === 'decisionNode') return;
        if (connectionMap[nodeId].length > 1) {
            issues.push(
                `Node "${nodeMap[nodeId]?.data?.label || nodeId}" has ${connectionMap[nodeId].length} outgoing connections. This might cause unpredictable behavior.`
            );
        }
    });
    return { valid: issues.length === 0, issues };
};

export const formatGenericTag = (tag: string) => {
    return tag.trim().toLowerCase().replace(/ /g, '_');
};

const reformatGenericTag = (tags: string) => {
    return tags.trim().toLowerCase().replace(/_/g, ' ');
};

export const parseGenericTags = (
    tags?: Record<string, { type: string; prompt: string; order?: number }>
): { value: string; type: string; order: number }[] => {
    if (!tags) return [{ value: '', type: 'ternary', order: 0 }];
    return Object.entries(tags)
        .map(([key, tag]) => ({
            value: reformatGenericTag(key),
            type: tag.type,
            order: tag.order ?? 0,
        }))
        .sort((a, b) => a.order - b.order);
};

export const DEFAULT_GENERIC_PROMPTS = {
    general_prompt: {
        promptName: 'General Prompt',
        value: '',
    },
    sanity_check_prompt: {
        promptName: 'Sanity Check Prompt',
        value: '',
    },
    unqualify_prompt: {
        promptName: 'Unqualify Prompt',
        value: '',
    },
    qualify_prompt: {
        promptName: 'Qualify Prompt',
        value: '',
    },
    undetermined_prompt: {
        promptName: 'Undetermined Prompt',
        value: '',
    },
};

export const parseGenericPrompts = (
    prompts?: Record<string, string>,
    tags: Record<string, { type: string; prompt: string }> = {}
) => {
    if (!prompts) return DEFAULT_GENERIC_PROMPTS;

    const generatedPromptsFromTags = Object.keys(tags).reduce(
        (acc: Record<string, { promptName: string; value: string }>, key: string) => {
            const formattedKey = reformatGenericTag(key);
            acc[key] = {
                promptName: formattedKey,
                value: tags[key].prompt,
            };
            return acc;
        },
        {}
    );

    const staticPrompts = Object.keys(prompts).reduce(
        (acc: Record<string, { promptName: string; value: string }>, key: string) => {
            const formattedKey = reformatGenericTag(key);
            acc[key] = {
                promptName: formattedKey,
                value: prompts[key],
            };
            return acc;
        },
        {}
    );

    return {
        ...DEFAULT_GENERIC_PROMPTS,
        ...generatedPromptsFromTags,
        ...staticPrompts,
    };
};
